import React, { useEffect, useState } from 'react';
import useYasna from '../yasna/useYasna';
import { entryView } from './entryView';
import './SenseCard.css';

/** "» value": kept together, so a long key wraps to "» value" below it. */
function Value({ children, className = 'sense-v' }) {
  return (
    <span className="sense-val">
      <span className="sense-sep" aria-hidden="true">»</span>
      <span className={className}>{children}</span>
    </span>
  );
}

/** "key » value", with its note under it. */
function Pair({ pair, onOpen }) {
  const key = pair.reference && onOpen ? (
    <button type="button" className="sense-k sense-k--link" onClick={() => onOpen(pair.reference)}>
      {pair.key}
    </button>
  ) : <span className="sense-k">{pair.key}</span>;
  return (
    <li className="sense-pair">
      <div className="sense-kv">
        {key}
        {pair.value && <Value>{pair.value}</Value>}
      </div>
      {pair.note && <div className="sense-note">{pair.note}</div>}
      {pair.example && (
        <div className="sense-kv sense-kv--example">
          <span className="sense-k">{pair.example.key}</span>
          {pair.example.value && <Value>{pair.example.value}</Value>}
        </div>
      )}
    </li>
  );
}

/**
 * The entry's grammar — its form ("3rd person singular, present tense of
 * imiter"), grammatical tags and grammar cards — collapsed under "Grammar"
 * for everyone (Requirement 56): hoja teaches from use, not terminology.
 */
function Grammar({ grammar, onOpen }) {
  const [open, setOpen] = useState(false);
  if (!grammar || (!grammar.form && grammar.tags.length === 0 && grammar.rows.length === 0)) return null;
  return (
    <section className="sense-grammar" aria-label="Grammar">
      <button type="button" className="sense-grammar-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        Grammar <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="sense-grammar-body">
          {grammar.form && (
            <div className="sense-row sense-kv">
              <span className="sense-label">form</span>
              <Value>{grammar.form}</Value>
            </div>
          )}
          {grammar.tags.length > 0 && (
            <ul className="sense-tags" aria-label="Grammar tags">
              {grammar.tags.map((t) => <li key={t} className="sense-tag">{t}</li>)}
            </ul>
          )}
          {grammar.rows.map((r, i) => <Row key={`${r.label}-${i}`} row={r} onOpen={onOpen} />)}
        </div>
      )}
    </section>
  );
}

// Teachers see every card; one students only get under "More" says so.
const UnderMore = () => <span className="sense-under-more">students: under More</span>;

function Row({ row, onOpen, marked }) {
  const cls = `sense-row${marked ? ' sense-row--more' : ''}`;
  if (row.value) {
    return (
      <div className={`${cls} sense-kv`}>
        <span className="sense-label">{row.label}</span>
        <Value>{row.value}</Value>
        {marked && <UnderMore />}
      </div>
    );
  }
  return (
    <section className={cls} aria-label={row.label}>
      <h4 className="sense-label">{row.label}{marked && <> <UnderMore /></>}</h4>
      <ul className="sense-pairs">
        {row.pairs.map((p, i) => <Pair key={i} pair={p} onOpen={onOpen} />)}
      </ul>
    </section>
  );
}

/**
 * SenseCard — a yasna sense drawn by hoja from yasna's raw entry
 * (getById), as key » value rows (Requirement 53). References and the
 * other senses of the same spelling open in place, with a back arrow.
 *
 * Students see what the teacher ticked "show to students" in yasna
 * (Requirement 53.3), the rest behind More. Teachers (`teacher`) see every
 * card, the ones students get only under More marked so (Requirement 54).
 *
 * Props:
 *   senseId — yasna word_sense_id
 *   teacher — show every card, marking what students see first
 *   onNavigate({ senseId, entry, depth }) — the shown sense changed
 */
function SenseCard({ senseId, teacher = false, onNavigate }) {
  const { yasna, error } = useYasna();
  const [stack, setStack] = useState([senseId]);
  const [entry, setEntry] = useState(undefined); // undefined: loading; null: not found
  const [others, setOthers] = useState([]);
  const [more, setMore] = useState(false);
  const current = stack[stack.length - 1];

  useEffect(() => {
    if (!yasna) return undefined;
    let cancelled = false;
    setEntry(undefined);
    setOthers([]);
    setMore(false);
    Promise.resolve(yasna.getById(current)).then(
      (e) => {
        if (cancelled) return;
        setEntry(e || null);
        if (e && onNavigate) onNavigate({ senseId: current, entry: e, depth: stack.length - 1 });
        if (e && typeof yasna.otherSenses === 'function') {
          Promise.resolve(yasna.otherSenses(e)).then(
            (list) => { if (!cancelled) setOthers(Array.isArray(list) ? list : []); },
            () => {}
          );
        }
      },
      () => { if (!cancelled) setEntry(null); }
    );
    return () => { cancelled = true; };
    // onNavigate is the page's state setter; stack changes come with current.
  }, [yasna, current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="sense-status sense-status--error">{error}</p>;
  if (!yasna || entry === undefined) return <p className="sense-status">Loading dictionary…</p>;

  const open = (id) => setStack((s) => [...s, id]);
  const back = stack.length > 1 && (
    <button type="button" className="sense-back" onClick={() => setStack((s) => s.slice(0, -1))}
      aria-label="Back to previous entry">←</button>
  );
  if (!entry) {
    return <div className="sense-card">{back}<p className="sense-status">This word isn't in the dictionary yet.</p></div>;
  }

  const view = entryView(entry);
  // Counted in cards, as yasna does: a grammar card is two rows.
  const cards = (list) => new Set(list.map((r) => r.card)).size;
  const hidden = cards(view.rows.filter((r) => !r.shown));
  const ticked = cards(view.rows.filter((r) => r.shown));
  const rows = more || teacher ? view.rows : view.rows.filter((r) => r.shown);
  const otherPairs = others
    .map((o) => ({ key: o.word, value: o.short_gloss || o.sense_label, reference: o.word_sense_id }))
    .filter((p) => p.key && p.reference);

  return (
    <div className="sense-card">
      <div className="sense-head">
        {back}
        <span className="sense-kv sense-kv--head">
          <span className="sense-word">{view.word}</span>
          {view.meaning && <Value className="sense-meaning">{view.meaning}</Value>}
        </span>
      </div>
      {view.tags.length > 0 && (
        <ul className="sense-tags" aria-label="Tags">
          {view.tags.map((t) => <li key={t} className="sense-tag">{t}</li>)}
        </ul>
      )}
      {teacher && (
        <p className="sense-students">
          {hidden > 0
            ? `Students see the meaning, tags and ${ticked} ticked card${ticked === 1 ? '' : 's'}; ${hidden} under More.`
            : 'Students see every card (none is ticked "show to students" in yasna).'}
        </p>
      )}
      {rows.map((r, i) => <Row key={`${r.label}-${i}`} row={r} onOpen={open} marked={teacher && !r.shown} />)}
      {!teacher && hidden > 0 && (
        <button type="button" className="sense-more" onClick={() => setMore((m) => !m)} aria-expanded={more}>
          {more ? 'Less' : `More (${hidden})`}
        </button>
      )}
      {otherPairs.length > 0 && (
        <Row row={{ label: 'also means', pairs: otherPairs }} onOpen={open} />
      )}
      <Grammar grammar={view.grammar} onOpen={open} />
    </div>
  );
}

export default SenseCard;
